import { makeHttpRequest } from '../makeHttpRequest/makeHttpRequest'
import { NetworkError } from '../errors/index'

/** The three possible response states */
export enum PingResponseStatus {
  OK = 'ping-ok',
  ERROR_RESPONSE = 'ping-error-response',
  NO_RESPONSE = 'ping-no-response',
  NO_PING_CONFIGURATION = 'no-ping-configuration'
}

/** The result returned by a ping */
export interface PingResult {
  status: PingResponseStatus;
  message?: string;
}

/** Type that a ping function must honor */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PingFunction = (...args: any[]) => Promise<PingResult>

/**
 * Use this method to check if the server is alive.
 * It calls a 'ping' command on the server (with no auth).
 *
 * The server must support a 'ping' endpoint at the url
 * that provides this functionality.

 * It returns a PingResult that you can inspect to see if the
 * response was:
 * - ok (200 server response)
 * - error-response (server responded with status code outside 200-range)
 * - no-response (no response from server)
 * - no-ping-configuration (the api has not been configured with a ping method)
 *
 * @param url
 */
export const ping: PingFunction = async (url: string) => {
  const res = await makeHttpRequest({
    url,
    method: 'post',
  })

  if (res.isOk()) {
    // eslint-disable-next-line no-console
    console.log(`PING-PONG! The server is alive at '${url}'! Response to ping: ${res.value}`)
    return {
      status: PingResponseStatus.OK,
      message: JSON.stringify(res.value),
    }
  }
  if (!(res.error instanceof NetworkError)) {
    // eslint-disable-next-line no-console
    console.log(`PING ERROR! the server at '${url}' responded,
    but with an error: ${res.error.message}`)
    return {
      status: PingResponseStatus.ERROR_RESPONSE,
      message: res.error.message,
    }
  }
  // eslint-disable-next-line no-console
  console.log(`PING ERROR! the server at '${url}' did NOT respond! ${res.error.message}`)
  return {
    status: PingResponseStatus.NO_RESPONSE,
    message: res.error.message,
  }
}

/**
 * A 'no ping' implementation of PingFunction
 * Can be used as default implementation in Apis when no ping method is
 * specified
 */
export const noPing: PingFunction = async () => {
  // eslint-disable-next-line no-console
  console.log('PING FAIL: no ping function has been configured for this api')
  return {
    status: PingResponseStatus.NO_PING_CONFIGURATION,
    message: 'no ping function has been configured for this api',
  }
}
